import React, { useState, useEffect } from 'react';
import { Form, Input, Upload, Modal, message, Progress } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { PlusOutlined } from '@ant-design/icons';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { uploadSingleFile, deleteFileByUrl } from '../../services/uploadService';

interface SectionFormProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (section: any) => void;
  initialValues?: any;
  loading?: boolean;
}

const SectionForm: React.FC<SectionFormProps> = ({
  visible,
  onCancel,
  onSubmit,
  initialValues,
  loading = false
}) => {
  const [form] = Form.useForm();
  const [imageList, setImageList] = useState<any[]>(initialValues?.images || []);
  const [imageList2, setImageList2] = useState<any[]>(initialValues?.images_2 || []);
  const [quillValue, setQuillValue] = useState(initialValues?.description || '');
  const [quillValueEn, setQuillValueEn] = useState(initialValues?.description_en || '');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isUploading2, setIsUploading2] = useState<boolean>(false);
  const [uploadPercent, setUploadPercent] = useState<number>(0);
  const [uploadPercent2, setUploadPercent2] = useState<number>(0);

  useEffect(() => {
    if (visible) {
      if (initialValues) {
        form.setFieldsValue(initialValues);
        setQuillValue(initialValues.description || '');
        setQuillValueEn(initialValues.description_en || '');
        setImageList(initialValues.images || []);
        setImageList2(initialValues.images_2 || []);

        // Set English name if available
        if (initialValues.name_en) {
          form.setFieldValue('name_en', initialValues.name_en);
        }

        // Set English description in form field
        if (initialValues.description_en) {
          form.setFieldValue('description_en', initialValues.description_en);
        }
      } else {
        form.resetFields();
        setQuillValue('');
        setQuillValueEn('');
        setImageList([]);
        setImageList2([]);
      }
    }
  }, [visible, initialValues, form]);

  useEffect(() => {
    form.setFieldsValue({ images: imageList });
  }, [imageList, form]);

  useEffect(() => {
    form.setFieldsValue({ images_2: imageList2 });
  }, [imageList2, form]);

  const handleUpload = async ({ file }: any) => {
    // Upload xử lý thủ công ở beforeUpload; onChange không cần làm gì thêm
    if (file.status === 'removed') return;
  };

  const handleUpload2 = async ({ file }: any) => {
    // Upload xử lý thủ công ở beforeUpload; onChange không cần làm gì thêm
    if (file.status === 'removed') return;
  };

  const uploadFilesParallel2 = async (files: File[]) => {
    if (!files || files.length === 0) return;
    setIsUploading2(true);
    setUploadPercent2(0);
    const hide = message.loading('Hệ thống đang xử lý ảnh 2, vui lòng đợi...', 0);
    const total = files.length;
    let completed = 0;
    const onDone = () => {
      completed += 1;
      setUploadPercent2(Math.round((completed / total) * 100));
      if (completed === total) {
        hide();
        setIsUploading2(false);
        setTimeout(() => setUploadPercent2(0), 300);
        message.success('Upload ảnh 2 hoàn tất');
      }
    };
    files.forEach(async (f) => {
      try {
        const res: any = await uploadSingleFile(f as any);
        if (res?.status === 'success') {
          const url = res.data.file.url;
          setImageList2(prev => [...prev, url]);
        } else {
          message.error(`Upload thất bại: ${f.name}`);
        }
      } catch (e) {
        console.error('Upload failed:', e);
        message.error(`Upload thất bại: ${f.name}`);
      } finally {
        onDone();
      }
    });
  };

  const handleRemove2 = async (file: any) => {
    try {
      const response = await deleteFileByUrl(file.url);
      if (response.status === 'success') {
        const newImageList = imageList2.filter((url: string) => url !== file.url);
        setImageList2(newImageList);
        message.success('File deleted successfully');
      } else {
        message.error('Failed to delete file');
      }
    } catch (error) {
      console.error('Delete failed:', error);
      message.error('Failed to delete file');
    }
    return true;
  };

  const uploadFilesParallel = async (files: File[]) => {
    if (!files || files.length === 0) return;
    setIsUploading(true);
    setUploadPercent(0);
    const hide = message.loading('Hệ thống đang xử lý ảnh, vui lòng đợi...', 0);
    const total = files.length;
    let completed = 0;
    const onDone = () => {
      completed += 1;
      setUploadPercent(Math.round((completed / total) * 100));
      if (completed === total) {
        hide();
        setIsUploading(false);
        setTimeout(() => setUploadPercent(0), 300);
        message.success('Upload hoàn tất');
      }
    };
    files.forEach(async (f) => {
      try {
        const res: any = await uploadSingleFile(f as any);
        if (res?.status === 'success') {
          const url = res.data.file.url;
          setImageList(prev => [...prev, url]);
        } else {
          message.error(`Upload thất bại: ${f.name}`);
        }
      } catch (e) {
        console.error('Upload failed:', e);
        message.error(`Upload thất bại: ${f.name}`);
      } finally {
        onDone();
      }
    });
  };

  const handleRemove = async (file: any) => {
    try {
      const response = await deleteFileByUrl(file.url);
      if (response.status === 'success') {
        const newImageList = imageList.filter((url: string) => url !== file.url);
        setImageList(newImageList);
        message.success('File deleted successfully');
      } else {
        message.error('Failed to delete file');
      }
    } catch (error) {
      console.error('Delete failed:', error);
      message.error('Failed to delete file');
    }
    return true;
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  // itemRender có thể thêm lại khi cần tùy biến hiển thị

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const sectionData = {
        ...values,
        description: quillValue,
        description_en: quillValueEn,
        images: imageList,
        images_2: imageList2
      };
      onSubmit(sectionData);
    } catch (error) {
      message.error('Vui lòng kiểm tra lại thông tin!');
    }
  };

  return (
    <Modal
      title={initialValues ? "Chỉnh sửa section" : "Thêm section mới"}
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={800}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
      >
        <Form.Item
          name="name"
          label="Tên section"
          rules={[
            { required: true, message: 'Vui lòng nhập tên section!' },
            { min: 2, message: 'Tên section phải có ít nhất 2 ký tự!' },
            { max: 100, message: 'Tên section không được quá 100 ký tự!' }
          ]}
        >
            <Input placeholder="Nhập tên section" />
        </Form.Item>

        <div style={{ marginLeft: '20px', marginTop: '-10px' }}>
          <Form.Item
            name="name_en"
            label="Tên section (tiếng Anh)"
            rules={[{ required: true, message: 'Vui lòng nhập tên section tiếng Anh' }]}
            style={{ marginBottom: '16px' }}
          >
            <Input
              placeholder="Enter section name in English"
              style={{
                width: '100%',
                height: 32,
                borderRadius: 4,
                fontSize: 16,
                padding: '6px 12px',
                backgroundColor: '#f5f5f5',
              }}
            />
          </Form.Item>
        </div>

        <Form.Item
          name="description"
          label="Mô tả"
          rules={[
            { required: true, message: 'Vui lòng nhập mô tả!' },
            // {
            //   validator: (_, value) => {
            //     const textContent = quillValue.replace(/<[^>]*>/g, '').trim();
            //     if (textContent.length < 10) {
            //       return Promise.reject(new Error('Mô tả phải có ít nhất 10 ký tự!'));
            //     }
            //     if (textContent.length > 2000) {
            //       return Promise.reject(new Error('Mô tả không được quá 2000 ký tự!'));
            //     }
            //     return Promise.resolve();
            //   }
            // }
          ]}
        >
          <ReactQuill
            theme='snow'
            value={quillValue}
            onChange={val => {
              setQuillValue(val);
            }}
            placeholder='Mô tả chi tiết về phần này'
            style={{
              borderRadius: '10px',
              height: '200px',
              marginBottom: '40px',
            }}
            modules={{
              toolbar: [
                [{ header: [1, 2, 3, false] }],
                ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                [
                  { list: 'ordered' },
                  { list: 'bullet' },
                  { indent: '-1' },
                  { indent: '+1' },
                ],
                ['link', 'image'],
                ['clean'],
              ],
            }}
            formats={[
              'header',
              'bold',
              'italic',
              'underline',
              'strike',
              'blockquote',
              'list',
              'indent',
              'link',
              'image',
            ]}
          />
        </Form.Item>

        <div style={{ marginLeft: '20px', marginTop: '40px' }}>
          <Form.Item
            name="description_en"
            label="Mô tả (tiếng Anh)"
            rules={[{ required: true, message: 'Vui lòng nhập mô tả tiếng Anh' }]}
            style={{ marginBottom: '40px' }}
          >
            <ReactQuill
              theme='snow'
              value={quillValueEn}
              onChange={val => {
                setQuillValueEn(val);
              }}
              placeholder='Enter detailed section description in English'
              style={{
                borderRadius: '10px',
                height: '200px',
                marginBottom: '40px',
                backgroundColor: '#f5f5f5',
              }}
              modules={{
                toolbar: [
                  [{ header: [1, 2, 3, false] }],
                  ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                  [
                    { list: 'ordered' },
                    { list: 'bullet' },
                    { indent: '-1' },
                    { indent: '+1' },
                  ],
                  ['link', 'image'],
                  ['clean'],
                ],
              }}
              formats={[
                'header',
                'bold',
                'italic',
                'underline',
                'strike',
                'blockquote',
                'list',
                'bullet',
                'indent',
                'link',
                'image',
              ]}
            />
          </Form.Item>
        </div>

        <Form.Item
          label="Images"
          name="images"
          rules={[{ required: true, message: 'Please upload at least one image' }]}
          getValueFromEvent={(e) => {
            if (Array.isArray(e)) {
              return e;
            }
            return imageList;
          }}
        >
          <Upload
            listType="picture-card"
            fileList={(imageList || [])
              .map((item: any, index: number): UploadFile => {
                const url: string = typeof item === 'string' ? item : (item?.url || item?.thumbUrl || '');
                return {
                  uid: `-${index}`,
                  name: (url && url.split('/').pop()) || `image-${index}`,
                  status: 'done',
                  url,
                  thumbUrl: url,
                } as UploadFile;
              })
              .filter((f: UploadFile) => !!f.url) as UploadFile[]}
            beforeUpload={(file, fileList) => {
              const first = fileList && fileList[0];
              if (first && (file as any).uid === (first as any).uid) {
                uploadFilesParallel(fileList as File[]);
              }
              return Upload.LIST_IGNORE;
            }}
            onChange={handleUpload}
            onRemove={handleRemove}
            disabled={isUploading}
            multiple
          >
            {isUploading ? (
              <div style={{ width: 140 }}>
                <div style={{ marginBottom: 8 }}>Đang xử lý...</div>
                <Progress percent={uploadPercent} size="small" status="active" />
              </div>
            ) : (
              imageList.length >= 8 ? null : uploadButton
            )}
          </Upload>
        </Form.Item>

        <Form.Item
          label="Images 2"
          name="images_2"
          rules={[{ required: true, message: 'Please upload at least one image' }]}
          getValueFromEvent={(e) => {
            if (Array.isArray(e)) {
              return e;
            }
            return imageList2;
          }}
        >
          <Upload
            listType="picture-card"
            fileList={(imageList2 || [])
              .map((item: any, index: number): UploadFile => {
                const url: string = typeof item === 'string' ? item : (item?.url || item?.thumbUrl || '');
                return {
                  uid: `-${index}`,
                  name: (url && url.split('/').pop()) || `image-${index}`,
                  status: 'done',
                  url,
                  thumbUrl: url,
                } as UploadFile;
              })
              .filter((f: UploadFile) => !!f.url) as UploadFile[]}
            beforeUpload={(file, fileList) => {
              const first = fileList && fileList[0];
              if (first && (file as any).uid === (first as any).uid) {
                uploadFilesParallel2(fileList as File[]);
              }
              return Upload.LIST_IGNORE;
            }}
            onChange={handleUpload2}
            onRemove={handleRemove2}
            disabled={isUploading2}
            multiple
          >
            {isUploading2 ? (
              <div style={{ width: 140 }}>
                <div style={{ marginBottom: 8 }}>Đang xử lý...</div>
                <Progress percent={uploadPercent2} size="small" status="active" />
              </div>
            ) : (
              imageList2.length >= 8 ? null : uploadButton
            )}
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default SectionForm;
